"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseMindsetCustomerIntegrationHandoff = parseMindsetCustomerIntegrationHandoff;
exports.parseMindsetCustomerIntegrationHandoffResponse = parseMindsetCustomerIntegrationHandoffResponse;
const customer_configuration_1 = require("./customer-configuration");
const customer_sdk_release_1 = require("./customer-sdk-release");
const sourceCommit = /^[0-9a-f]{40}$/;
const digest = /^[0-9a-f]{64}$/;
const requiredFields = [
    "schemaVersion",
    "environment",
    "tenantId",
    "applicationId",
    "gatewayUrl",
    "realtimeUrl",
];
function exact(input, keys, label) {
    if (!input || typeof input !== "object" || Array.isArray(input))
        throw new TypeError(`Invalid ${label}`);
    const value = input;
    if (Object.keys(value).length !== keys.length || keys.some(key => !Object.prototype.hasOwnProperty.call(value, key))) {
        throw new TypeError(`Invalid ${label} fields`);
    }
    return value;
}
function strings(value) {
    if (!Array.isArray(value) || value.length > 20 || value.some(item => typeof item !== "string" || item.length < 1 || item.length > 400)) {
        throw new TypeError("Invalid handoff list");
    }
    return [...value];
}
function recordOfStrings(value) {
    if (!value || typeof value !== "object" || Array.isArray(value) || Object.keys(value).length > 10) {
        throw new TypeError("Invalid handoff shape");
    }
    return Object.fromEntries(Object.entries(value).map(([key, item]) => {
        if (!/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(key) || typeof item !== "string" || item.length < 1 || item.length > 500) {
            throw new TypeError("Invalid handoff shape");
        }
        return [key, item];
    }));
}
function callback(value) {
    const item = exact(value, ["owner", "requiredShape", "controls"], "handoff callback");
    if (item.owner !== "customer-host")
        throw new TypeError("Invalid handoff callback");
    return { owner: "customer-host", requiredShape: recordOfStrings(item.requiredShape), controls: strings(item.controls) };
}
function gate(value) {
    const item = exact(value, ["id", "command", "status", "passFailStatus"], "handoff gate");
    if (typeof item.id !== "string" || !/^[a-z0-9-]{3,80}$/.test(item.id) ||
        typeof item.command !== "string" || item.command.length < 1 || item.command.length > 500 ||
        item.status !== "referenced-local-gate" || item.passFailStatus !== "not-run-by-this-command") {
        throw new TypeError("Invalid handoff gate");
    }
    return { id: item.id, command: item.command, status: "referenced-local-gate",
        passFailStatus: "not-run-by-this-command" };
}
function blocker(value) {
    const item = exact(value, ["id", "summary", "classification", "status"], "handoff blocker");
    if (typeof item.id !== "string" || !/^[a-z0-9-]{3,80}$/.test(item.id) ||
        typeof item.summary !== "string" || item.summary.length < 1 || item.summary.length > 500 ||
        item.classification !== "protectedExternal" || item.status !== "blocked") {
        throw new TypeError("Invalid handoff blocker");
    }
    return { id: item.id, summary: item.summary, classification: "protectedExternal", status: "blocked" };
}
function controls(value) {
    const item = exact(value, ["appNeutral", "noNamedCustomerDependency", "noProviderCall", "noSecretRead", "noDeploy", "noPublishOrPush", "productionAcceptanceFalse"], "handoff controls");
    if (!Object.values(item).every(flag => flag === true))
        throw new TypeError("Invalid handoff controls");
    return item;
}
function scanNeutral(value) {
    const text = JSON.stringify(value);
    if (/\b(?:terms|youtool|ourterms)\b/i.test(text) ||
        /(?:bucket|objectName|generation|tenant_key|msk_tn_|Bearer\s+[A-Za-z0-9._~+/=-]{8,}|sk_live_|sk_test_|rk_live_|rk_test_)/i.test(text)) {
        throw new TypeError("Invalid handoff neutrality");
    }
}
function parseMindsetCustomerIntegrationHandoff(input) {
    const value = exact(input, [
        "schemaVersion",
        "evidenceType",
        "sourceCommit",
        "releaseVersion",
        "productionAcceptance",
        "customerConfiguration",
        "release",
        "requiredCustomerConfigurationFields",
        "callbackContracts",
        "setupSteps",
        "localSmokeValidation",
        "rollbackAndDisable",
        "troubleshooting",
        "remainingProtectedExternalBlockers",
        "controls",
        "evidenceSha256",
        "markdown",
    ], "handoff");
    if (value.schemaVersion !== "mindset.customer-integration-handoff/v1" ||
        value.evidenceType !== "mindset.customer-integration-handoff" ||
        typeof value.sourceCommit !== "string" || !sourceCommit.test(value.sourceCommit) ||
        typeof value.releaseVersion !== "string" || !/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/.test(value.releaseVersion) ||
        value.productionAcceptance !== false ||
        typeof value.evidenceSha256 !== "string" || !digest.test(value.evidenceSha256) ||
        typeof value.markdown !== "string" || value.markdown.length < 1 || value.markdown.length > 20000) {
        throw new TypeError("Invalid handoff");
    }
    if (!Array.isArray(value.requiredCustomerConfigurationFields) ||
        value.requiredCustomerConfigurationFields.length !== requiredFields.length ||
        !requiredFields.every((field, index) => value.requiredCustomerConfigurationFields[index] === field)) {
        throw new TypeError("Invalid handoff configuration fields");
    }
    const callbacks = exact(value.callbackContracts, ["authenticatedUserToken", "permissionPolicy"], "handoff callbacks");
    const setup = exact(value.setupSteps, ["web", "reactNative", "android", "ios"], "handoff setup");
    if (!Array.isArray(value.troubleshooting) || value.troubleshooting.length > 20)
        throw new TypeError("Invalid handoff troubleshooting");
    const parsed = {
        schemaVersion: "mindset.customer-integration-handoff/v1",
        evidenceType: "mindset.customer-integration-handoff",
        sourceCommit: value.sourceCommit,
        releaseVersion: value.releaseVersion,
        productionAcceptance: false,
        customerConfiguration: (0, customer_configuration_1.parseMindsetCustomerConfiguration)(value.customerConfiguration),
        release: (0, customer_sdk_release_1.parseMindsetCustomerSdkRelease)(value.release),
        requiredCustomerConfigurationFields: [...requiredFields],
        callbackContracts: {
            authenticatedUserToken: callback(callbacks.authenticatedUserToken),
            permissionPolicy: callback(callbacks.permissionPolicy),
        },
        setupSteps: { web: strings(setup.web), reactNative: strings(setup.reactNative), android: strings(setup.android), ios: strings(setup.ios) },
        rollbackAndDisable: strings(value.rollbackAndDisable),
        troubleshooting: value.troubleshooting.map(item => {
            const entry = exact(item, ["id", "response"], "handoff troubleshooting");
            if (typeof entry.id !== "string" || !/^[a-z0-9-]{3,80}$/.test(entry.id) ||
                typeof entry.response !== "string" || entry.response.length < 1 || entry.response.length > 500)
                throw new TypeError("Invalid handoff troubleshooting");
            return { id: entry.id, response: entry.response };
        }),
        localSmokeValidation: Array.isArray(value.localSmokeValidation) ? value.localSmokeValidation.map(gate) : [],
        remainingProtectedExternalBlockers: Array.isArray(value.remainingProtectedExternalBlockers) ? value.remainingProtectedExternalBlockers.map(blocker) : [],
        controls: controls(value.controls),
        evidenceSha256: value.evidenceSha256,
        markdown: value.markdown,
    };
    if (parsed.sourceCommit !== parsed.release.sourceCommit || parsed.releaseVersion !== parsed.release.version ||
        parsed.customerConfiguration.applicationId.length < 1 || parsed.localSmokeValidation.length < 1 ||
        parsed.remainingProtectedExternalBlockers.length < 1) {
        throw new TypeError("Invalid handoff binding");
    }
    scanNeutral(parsed);
    return parsed;
}
function parseMindsetCustomerIntegrationHandoffResponse(input) {
    return parseMindsetCustomerIntegrationHandoff(exact(input, ["handoff"], "handoff response").handoff);
}
//# sourceMappingURL=customer-integration-handoff.js.map